import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Match, MatchWithGames, PlayerStats, TeamStats, GameMode } from '@/types';
import { matchRepo } from '@/db/repositories/matchRepo';
import { teamRepo } from '@/db/repositories/teamRepo';
import { playerRepo } from '@/db/repositories/playerRepo';
import { roundRepo } from '@/db/repositories/roundRepo';

interface HistoryState {
  completedMatches: Match[];
  isLoading: boolean;
  error: string | null;
}

interface HistoryActions {
  loadCompletedMatches: () => Promise<void>;
  getMatchWithGames: (matchId: string) => Promise<MatchWithGames | null>;
  deleteMatch: (matchId: string) => Promise<boolean>;
  getPlayerStats: (playerId: string) => Promise<PlayerStats | null>;
  getAllPlayerStats: () => Promise<PlayerStats[]>;
  getAllTeamStats: () => Promise<TeamStats[]>;
  clearError: () => void;
}

type HistoryStore = HistoryState & HistoryActions;

export const useHistoryStore = create<HistoryStore>()(
  immer((set) => ({
    // State
    completedMatches: [],
    isLoading: false,
    error: null,

    // Actions
    loadCompletedMatches: async () => {
      set((state) => {
        state.isLoading = true;
        state.error = null;
      });

      try {
        const matches = await matchRepo.getCompleted();
        set((state) => {
          state.completedMatches = matches;
          state.isLoading = false;
        });
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error ? err.message : 'Failed to load history';
          state.isLoading = false;
        });
      }
    },

    getMatchWithGames: async (matchId: string) => {
      try {
        return await matchRepo.getWithGames(matchId);
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error ? err.message : 'Failed to load match';
        });
        return null;
      }
    },

    deleteMatch: async (matchId: string) => {
      try {
        const deleted = await matchRepo.delete(matchId);
        if (deleted) {
          set((state) => {
            state.completedMatches = state.completedMatches.filter(
              (m) => m.id !== matchId
            );
          });
          return true;
        }
        return false;
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error ? err.message : 'Failed to delete match';
        });
        return false;
      }
    },

    getPlayerStats: async (playerId: string) => {
      try {
        const allMatchesWithGames = await matchRepo.getAllWithGames();
        const completedMatches = allMatchesWithGames.filter(
          (m) => m.status === 'completed'
        );

        // Find matches where player participated
        const playerMatches = completedMatches.filter((match) =>
          match.participants.some(
            (p) =>
              p.id === playerId ||
              (p.type === 'team' && p.playerIds?.includes(playerId))
          )
        );

        if (playerMatches.length === 0) {
          return null;
        }

        let totalPoints = 0;
        let totalRegularPoints = 0;
        let totalDeclarationPoints = 0;
        let matchesWon = 0;
        let gamesPlayed = 0;
        let gamesWon = 0;
        const byMode: Map<
          GameMode,
          {
            matchesPlayed: number;
            matchesWon: number;
            gamesPlayed: number;
            gamesWon: number;
          }
        > = new Map();

        for (const match of playerMatches) {
          // Find the participant for this player
          const participant = match.participants.find(
            (p) =>
              p.id === playerId ||
              (p.type === 'team' && p.playerIds?.includes(playerId))
          );

          if (!participant) continue;

          // Track match win
          if (match.winnerId === participant.id) {
            matchesWon++;
          }

          // Process games within this match
          for (const game of match.games.filter(
            (g) => g.status === 'completed'
          )) {
            gamesPlayed++;

            // Calculate points from this game's rounds
            const gameRounds = await roundRepo.getByGameId(game.id);
            for (const round of gameRounds) {
              const score = round.scores.find(s => s.participantId === participant.id);
              if (score) {
                totalRegularPoints += score.regularPoints;
                totalDeclarationPoints += score.declarationPoints;
                totalPoints += score.regularPoints + score.declarationPoints;
              }
            }

            // Check if player won this game
            if (game.winnerId === participant.id) {
              gamesWon++;
            }
          }

          // Track by mode
          const modeStats = byMode.get(match.mode) || {
            matchesPlayed: 0,
            matchesWon: 0,
            gamesPlayed: 0,
            gamesWon: 0,
          };
          modeStats.matchesPlayed++;
          if (match.winnerId === participant.id) {
            modeStats.matchesWon++;
          }
          const modeGames = match.games.filter((g) => g.status === 'completed');
          modeStats.gamesPlayed += modeGames.length;
          modeStats.gamesWon += modeGames.filter(
            (g) => g.winnerId === participant.id
          ).length;
          byMode.set(match.mode, modeStats);
        }

        const matchesPlayed = playerMatches.length;

        return {
          playerId,
          playerName: '', // Will be filled by caller
          matchesPlayed,
          matchesWon,
          matchWinRate:
            matchesPlayed > 0 ? (matchesWon / matchesPlayed) * 100 : 0,
          gamesPlayed,
          gamesWon,
          gameWinRate: gamesPlayed > 0 ? (gamesWon / gamesPlayed) * 100 : 0,
          totalPoints,
          totalRegularPoints,
          totalDeclarationPoints,
          averagePointsPerGame: gamesPlayed > 0 ? totalPoints / gamesPlayed : 0,
          averageRegularPointsPerGame: gamesPlayed > 0 ? totalRegularPoints / gamesPlayed : 0,
          averageDeclarationPointsPerGame: gamesPlayed > 0 ? totalDeclarationPoints / gamesPlayed : 0,
          byMode: Array.from(byMode.entries()).map(([mode, stats]) => ({
            mode,
            ...stats,
          })),
        };
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error
              ? err.message
              : 'Failed to calculate statistics';
        });
        return null;
      }
    },

    getAllPlayerStats: async () => {
      try {
        const allMatchesWithGames = await matchRepo.getAllWithGames();
        const completedMatches = allMatchesWithGames.filter(
          (m) => m.status === 'completed'
        );

        // Load all players for name lookup
        const allPlayers = await playerRepo.getAll();
        const playerNameMap = new Map(allPlayers.map((p) => [p.id, p.name]));

        // Build a map of all player IDs and their stats
        const playerStatsMap: Map<
          string,
          {
            name: string;
            participantId: string; // The participant ID (player or team) for this player
            matchesPlayed: number;
            matchesWon: number;
            gamesPlayed: number;
            gamesWon: number;
            totalPoints: number;
            totalRegularPoints: number;
            totalDeclarationPoints: number;
            byMode: Map<
              GameMode,
              {
                matchesPlayed: number;
                matchesWon: number;
                gamesPlayed: number;
                gamesWon: number;
              }
            >;
          }
        > = new Map();

        // First pass: collect all player stats and participant mappings
        for (const match of completedMatches) {
          for (const participant of match.participants) {
            // For teams, get individual player IDs; for players, use their ID directly
            const playerIds =
              participant.type === 'team' && participant.playerIds
                ? participant.playerIds
                : [participant.id];

            for (const playerId of playerIds) {
              if (!playerStatsMap.has(playerId)) {
                // Use actual player name from lookup, fallback to participant name for non-team
                const playerName =
                  playerNameMap.get(playerId) ??
                  (participant.type === 'player' ? participant.name : 'Unknown Player');
                playerStatsMap.set(playerId, {
                  name: playerName,
                  participantId: participant.id,
                  matchesPlayed: 0,
                  matchesWon: 0,
                  gamesPlayed: 0,
                  gamesWon: 0,
                  totalPoints: 0,
                  totalRegularPoints: 0,
                  totalDeclarationPoints: 0,
                  byMode: new Map(),
                });
              }

              const stats = playerStatsMap.get(playerId)!;
              stats.matchesPlayed++;

              // Check match win
              if (match.winnerId === participant.id) {
                stats.matchesWon++;
              }

              // Process games
              const completedGames = match.games.filter(
                (g) => g.status === 'completed'
              );
              stats.gamesPlayed += completedGames.length;
              stats.gamesWon += completedGames.filter(
                (g) => g.winnerId === participant.id
              ).length;

              // Load rounds for each game and calculate points
              for (const game of completedGames) {
                const rounds = await roundRepo.getByGameId(game.id);
                for (const round of rounds) {
                  const score = round.scores.find(s => s.participantId === participant.id);
                  if (score) {
                    stats.totalRegularPoints += score.regularPoints;
                    stats.totalDeclarationPoints += score.declarationPoints;
                    stats.totalPoints += score.regularPoints + score.declarationPoints;
                  }
                }
              }

              // Track by mode
              const modeStats = stats.byMode.get(match.mode) || {
                matchesPlayed: 0,
                matchesWon: 0,
                gamesPlayed: 0,
                gamesWon: 0,
              };
              modeStats.matchesPlayed++;
              if (match.winnerId === participant.id) {
                modeStats.matchesWon++;
              }
              modeStats.gamesPlayed += completedGames.length;
              modeStats.gamesWon += completedGames.filter(
                (g) => g.winnerId === participant.id
              ).length;
              stats.byMode.set(match.mode, modeStats);
            }
          }
        }

        return Array.from(playerStatsMap.entries())
          .map(([playerId, stats]) => ({
            playerId,
            playerName: stats.name,
            matchesPlayed: stats.matchesPlayed,
            matchesWon: stats.matchesWon,
            matchWinRate:
              stats.matchesPlayed > 0
                ? (stats.matchesWon / stats.matchesPlayed) * 100
                : 0,
            gamesPlayed: stats.gamesPlayed,
            gamesWon: stats.gamesWon,
            gameWinRate:
              stats.gamesPlayed > 0
                ? (stats.gamesWon / stats.gamesPlayed) * 100
                : 0,
            totalPoints: stats.totalPoints,
            totalRegularPoints: stats.totalRegularPoints,
            totalDeclarationPoints: stats.totalDeclarationPoints,
            averagePointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalPoints / stats.gamesPlayed
                : 0,
            averageRegularPointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalRegularPoints / stats.gamesPlayed
                : 0,
            averageDeclarationPointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalDeclarationPoints / stats.gamesPlayed
                : 0,
            byMode: Array.from(stats.byMode.entries()).map(([mode, m]) => ({
              mode,
              ...m,
            })),
          }))
          .sort((a, b) => b.matchWinRate - a.matchWinRate);
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error
              ? err.message
              : 'Failed to calculate statistics';
        });
        return [];
      }
    },

    getAllTeamStats: async () => {
      try {
        const allMatchesWithGames = await matchRepo.getAllWithGames();
        const completedMatches = allMatchesWithGames.filter(
          (m) => m.status === 'completed' && m.mode === '4_player'
        );

        // Get all teams for name lookup
        const allTeams = await teamRepo.getAllWithPlayers();
        const teamMap = new Map(allTeams.map((t) => [t.id, t]));

        // Build a map of team stats
        const teamStatsMap: Map<
          string,
          {
            teamName: string;
            player1Name: string;
            player2Name: string;
            participantId: string;
            matchesPlayed: number;
            matchesWon: number;
            gamesPlayed: number;
            gamesWon: number;
            totalPoints: number;
            totalRegularPoints: number;
            totalDeclarationPoints: number;
          }
        > = new Map();

        for (const match of completedMatches) {
          for (const participant of match.participants) {
            // Only track stats for saved teams (those with teamId)
            if (participant.type !== 'team' || !participant.teamId) {
              continue;
            }

            const teamId = participant.teamId;
            const team = teamMap.get(teamId);

            if (!teamStatsMap.has(teamId)) {
              teamStatsMap.set(teamId, {
                teamName: team?.name ?? participant.name,
                player1Name: team?.player1.name ?? '',
                player2Name: team?.player2.name ?? '',
                participantId: participant.id,
                matchesPlayed: 0,
                matchesWon: 0,
                gamesPlayed: 0,
                gamesWon: 0,
                totalPoints: 0,
                totalRegularPoints: 0,
                totalDeclarationPoints: 0,
              });
            }

            const stats = teamStatsMap.get(teamId)!;
            stats.matchesPlayed++;

            // Check match win
            if (match.winnerId === participant.id) {
              stats.matchesWon++;
            }

            // Process games
            const completedGames = match.games.filter(
              (g) => g.status === 'completed'
            );
            stats.gamesPlayed += completedGames.length;
            stats.gamesWon += completedGames.filter(
              (g) => g.winnerId === participant.id
            ).length;

            // Load rounds for each game and calculate points
            for (const game of completedGames) {
              const rounds = await roundRepo.getByGameId(game.id);
              for (const round of rounds) {
                const score = round.scores.find(s => s.participantId === participant.id);
                if (score) {
                  stats.totalRegularPoints += score.regularPoints;
                  stats.totalDeclarationPoints += score.declarationPoints;
                  stats.totalPoints += score.regularPoints + score.declarationPoints;
                }
              }
            }
          }
        }

        return Array.from(teamStatsMap.entries())
          .map(([teamId, stats]) => ({
            teamId,
            teamName: stats.teamName,
            player1Name: stats.player1Name,
            player2Name: stats.player2Name,
            matchesPlayed: stats.matchesPlayed,
            matchesWon: stats.matchesWon,
            matchWinRate:
              stats.matchesPlayed > 0
                ? (stats.matchesWon / stats.matchesPlayed) * 100
                : 0,
            gamesPlayed: stats.gamesPlayed,
            gamesWon: stats.gamesWon,
            gameWinRate:
              stats.gamesPlayed > 0
                ? (stats.gamesWon / stats.gamesPlayed) * 100
                : 0,
            totalPoints: stats.totalPoints,
            totalRegularPoints: stats.totalRegularPoints,
            totalDeclarationPoints: stats.totalDeclarationPoints,
            averagePointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalPoints / stats.gamesPlayed
                : 0,
            averageRegularPointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalRegularPoints / stats.gamesPlayed
                : 0,
            averageDeclarationPointsPerGame:
              stats.gamesPlayed > 0
                ? stats.totalDeclarationPoints / stats.gamesPlayed
                : 0,
          }))
          .sort((a, b) => b.matchWinRate - a.matchWinRate);
      } catch (err) {
        set((state) => {
          state.error =
            err instanceof Error
              ? err.message
              : 'Failed to calculate team statistics';
        });
        return [];
      }
    },

    clearError: () => {
      set((state) => {
        state.error = null;
      });
    },
  }))
);
